<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link :to="{ name: 'cost-prices-rules' }" class="d-flex align-items-center gap-2">
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to List') }}
      </router-link>
    </div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="customConditionForm">
        <b-form ref="customConditionFormElement" @submit.prevent="submit">
          <b-row>
            <b-col sm="12" md="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="provider">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Provider')"
                  vid="providerId"
                  :rules="type === 'edit' ? '' : 'required'"
                >
                  <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                  <v-select
                    v-else
                    id="provider"
                    :disabled="type === 'edit'"
                    v-model="values.providerId"
                    :options="providerOptions"
                    :reduce="item => item.value"
                    :placeholder="t('Make a Choice')"
                    label="label"
                    :tabindex="2"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Rule Type Selection -->
            <b-col cols="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="rule_type">{{ t('Rule Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Rule Type')"
                  vid="type"
                  rules="required"
                >
                  <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                  <v-select
                    v-else
                    id="rule_type"
                    v-model="values.ruleType"
                    :options="ruleTypeOptions"
                    :reduce="item => item.value"
                    :placeholder="t('Make a Choice')"
                    label="label"
                    @input="values.ruleOperator = null"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Rule Information Section -->
            <b-col cols="12">
              <div class="condition-section rule-info-section">
                <h6 class="section-title">{{ t('Rule Information') }}</h6>
                <b-row>
                  <b-col sm="12" md="6">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="operator">{{ t('Operator') }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Operator')"
                        vid="operator"
                        rules="required"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <v-select
                          v-else
                          id="operator"
                          v-model="values.ruleOperator"
                          :options="filteredOperatorOptions"
                          :reduce="item => item.value"
                          :placeholder="t('Make a Choice')"
                          label="label"
                        >
                          <span slot="no-options">{{ t('No records found') }}</span>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- When Rule Type is Alıcı Ülke (6): Multi Country Select -->
                  <b-col v-if="isRecipientCountry" sm="12" md="6">
                    <b-form-group>
                      <div
                        class="d-flex justify-content-between align-items-center form-group-label mb-50"
                      >
                        <div class="d-flex align-items-center">
                          <label for="countries" class="mb-0 mr-25">{{ t('Countries') }}</label>
                          <span class="text-danger mr-50">*</span>
                        </div>
                        <b-button
                          v-if="values.countries && values.countries.length > 0"
                          variant="flat-danger"
                          size="sm"
                          class="p-0 text-danger font-small-2"
                          type="button"
                          @click="values.countries = []"
                        >
                          <feather-icon icon="Trash2Icon" size="12" class="mr-25" />
                          {{ t('Clear All') }}
                        </b-button>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Countries')"
                        vid="countries"
                        rules="required"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <v-select
                          v-else
                          id="countries"
                          v-model="values.countries"
                          :options="countryOptions"
                          :reduce="item => item.value"
                          :placeholder="t('Search and select countries...')"
                          label="label"
                          multiple
                          :searchable="true"
                          :clearable="true"
                          :close-on-select="false"
                          class="countries-select"
                        >
                          <span slot="no-options">{{ t('No records found') }}</span>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- When Rule Type is NOT Alıcı Ülke: Value input -->
                  <b-col v-else sm="12" md="6">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="kg_desi_value">{{ t('Fee Value') }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Fee Value')"
                        vid="value"
                        rules="required"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <b-form-input
                          v-else
                          id="kg_desi_value"
                          v-model="values.ruleValue"
                          type="number"
                          step="0.001"
                          :min="0"
                          :placeholder="t('Enter Value')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-col>

            <!-- Price Information Section -->
            <b-col cols="12">
              <div class="condition-section price-info-section">
                <h6 class="section-title">{{ t('Price Information') }}</h6>
                <b-row>
                  <b-col sm="12" md="4">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="price_type">{{ t('Price Type') }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Price Type')"
                        vid="pricingType"
                        rules="required"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <v-select
                          v-else
                          id="price_type"
                          v-model="values.priceType"
                          :options="priceTypeOptions"
                          :reduce="item => item.value"
                          :placeholder="t('Make a Choice')"
                          label="label"
                        >
                          <span slot="no-options">{{ t('No records found') }}</span>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col sm="12" md="4">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="price_value">{{ priceLabel }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Price')"
                        vid="price"
                        rules="required|doubleOrInteger|positive"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <b-form-input
                          v-else
                          id="price_value"
                          v-model="values.priceValue"
                          type="number"
                          :step="0.001"
                          :min="0"
                          :placeholder="t('Enter Value')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col sm="12" md="4">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="minimum_price">{{ t('Minimum Price') }}</label>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Minimum Price')"
                        vid="minimumPrice"
                        rules="doubleOrInteger|positive"
                      >
                        <b-skeleton v-if="isFieldLoading" height="38px" class="rounded" />
                        <b-form-input
                          v-else
                          id="minimum_price"
                          v-model="values.minimumPrice"
                          type="number"
                          :step="0.001"
                          :min="0"
                          :placeholder="t('Enter Minimum Price')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-3"
                style="gap: 12px"
              >
                <b-button variant="primary" class="d-flex align-items-center" type="submit">
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BRow,
  BButton,
  BOverlay,
  BFormInput,
  BSkeleton
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import vSelect from 'vue-select';
import useService from '@/auth/service/useService';

export default {
  name: 'CostPriceCustomConditionForm',
  components: {
    BOverlay,
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BSkeleton,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },

  mixins: [helper],
  props: {
    submitting: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'new'
    },
    values: {
      type: Object,
      default: () => ({
        ruleType: null,
        ruleOperator: null,
        ruleValue: null,
        priceType: null,
        priceValue: null,
        providerId: null,
        countries: [],
        minimumPrice: null
      })
    }
  },

  data() {
    return {
      loadingOptions: true,
      ruleTypeOptions: [],
      operatorOptions: [],
      priceTypeOptions: [],
      providerOptions: []
    };
  },

  computed: {
    isFieldLoading() {
      if (this.type !== 'edit') return false;
      return (
        this.loadingOptions || this.values.ruleType === undefined || this.values.ruleType === null
      );
    },

    isRecipientCountry() {
      return Number(this.values.ruleType) === 6;
    },

    filteredOperatorOptions() {
      if (this.isRecipientCountry) {
        return this.operatorOptions.filter(item => [7, 8].includes(Number(item.value)));
      }
      return this.operatorOptions.filter(item => ![7, 8].includes(Number(item.value)));
    },

    countryOptions() {
      return (this.$store?.getters['app/countries'] || []).map(item => ({
        value: item.value,
        label: `${item.text} (${item.iso2})`
      }));
    },

    priceLabel() {
      return Number(this.values.priceType) === 3 ? this.t('Price per Kg/Desi') : this.t('Price');
    }
  },

  methods: {
    submit() {
      this.$refs.customConditionForm.validate().then(success => {
        if (!success) return;

        const formData = {
          providerId: this.values.providerId,
          type: Number(this.values.ruleType),
          operator: Number(this.values.ruleOperator),
          pricingType: Number(this.values.priceType),
          price: Number(this.values.priceValue)
        };

        if (this.isRecipientCountry) {
          formData.countries = (this.values.countries || []).map(Number);
        } else {
          formData.value = Number(this.values.ruleValue);
        }

        if (this.values.minimumPrice != null && this.values.minimumPrice !== '') {
          formData.minimumPrice = Number(this.values.minimumPrice);
        }

        this.$emit('submit', formData);
      });
    }
  },

  async mounted() {
    this.loadingOptions = true;
    try {
      this.$store.dispatch('app/getCountries');

      const [providerRes, ruleTypeRes, operatorRes, priceTypeRes] = await Promise.all([
        useService.providerService.getAllProviders({ paginated: false }),
        useService.constantService.getProviderRuleType(),
        useService.constantService.getProviderRuleOperator(),
        useService.constantService.getProviderRulePricingType()
      ]);

      this.providerOptions = (providerRes?.data || []).map(item => ({
        value: item.id,
        label: item.name
      }));
      this.ruleTypeOptions = (ruleTypeRes?.data || []).map(item => ({
        value: item.value,
        label: item.info
      }));
      this.operatorOptions = (operatorRes?.data || []).map(item => ({
        value: item.value,
        label: item.info
      }));
      this.priceTypeOptions = (priceTypeRes?.data || []).map(item => ({
        value: item.value,
        label: item.info
      }));
    } finally {
      this.loadingOptions = false;
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss" scoped>
.form-group-label {
  margin-bottom: 0.5rem;
}

small.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.condition-section {
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;

  .section-title {
    margin-bottom: 1rem;
    color: #5e5873;
    font-weight: 600;
  }
}

.rule-info-section {
  position: relative;
  z-index: 2;
}

.price-info-section {
  position: relative;
  z-index: 1;
}

::v-deep .countries-select {
  .vs__selected-options {
    max-height: 120px;
    overflow-y: auto;
  }

  .vs__dropdown-menu {
    max-height: 240px;
    z-index: 1050;
  }
}

// Dark mode support
body.dark-layout {
  .condition-section {
    background-color: #2d3748;
  }
}
</style>
